import {
  SoftwareRecoveryCustodian,
  clearBytes,
  createDeviceAuthorizationToken,
  createDeviceSecret,
  createExportableRecoveryCustodian,
  createPinEnvelope,
  createRecoveryContactProvisioning,
  createRecoveryDraftProvisioning,
  createRecoveryRecipient,
  decodeBytes,
  encodeBytes,
  hashDeviceAuthorizationToken,
  importRecoveryCustodian,
  nowIso,
  openLocalRecoveryDraft,
  openRecoveryCustodianPrivateKey,
  protectRecoveryCustodianPrivateKey,
  recoverVaultMaterial,
  recoveryKeyFingerprint,
  unlockVaultRootKey,
  verifyRecoveryContactPackage,
  type RecoveryApprovalContext,
  type RecoveryEnvelope,
  type SealedRecoveryValue,
  type VaultProtectedRecoveryKey,
} from '@safewallet/core';
import { loadDeviceSecret } from './device-secret';
import type { DeviceState } from './device-store';
import { saveDeviceState } from './device-store';
import {
  clearPendingRecovery,
  clearRecoveryDraft,
  loadPendingRecovery,
  loadRecoveryDraft,
  savePendingRecovery,
  saveRecoveryDraft,
} from './recovery-store';
import { supabase } from './supabase';

function client() {
  if (!supabase) throw new Error('Cloud synchronization is not configured.');
  return supabase;
}

export interface FreshAuthResult {
  needsMfa: boolean;
  totpFactors: Array<{ id: string; name: string }>;
}

export async function sendFreshAuthCode(email: string): Promise<void> {
  const normalized = email.trim().toLowerCase();
  if (!normalized) throw new Error('Enter your account email.');
  const { error } = await client().auth.signInWithOtp({
    email: normalized,
    options: { shouldCreateUser: false },
  });
  if (error) throw error;
}

export async function verifyFreshAuthCode(email: string, code: string): Promise<FreshAuthResult> {
  const { error } = await client().auth.verifyOtp({
    email: email.trim().toLowerCase(),
    token: code.trim(),
    type: 'email',
  });
  if (error) throw error;
  const { data: aal, error: aalError } = await client().auth.mfa.getAuthenticatorAssuranceLevel();
  if (aalError) throw aalError;
  if (aal.nextLevel !== 'aal2' || aal.currentLevel === 'aal2') return { needsMfa: false, totpFactors: [] };
  const { data: factors, error: factorError } = await client().auth.mfa.listFactors();
  if (factorError) throw factorError;
  return {
    needsMfa: true,
    totpFactors: factors.totp
      .filter((factor) => factor.status === 'verified')
      .map((factor) => ({ id: factor.id, name: factor.friendly_name || 'Authenticator app' })),
  };
}

export async function verifyFreshAuthMfa(factorId: string, code: string): Promise<void> {
  const { error } = await client().auth.mfa.challengeAndVerify({ factorId, code: code.trim() });
  if (error) throw error;
}

export async function currentAccountIdentity(): Promise<{ userId: string; email: string }> {
  const { data, error } = await client().auth.getUser();
  if (error) throw error;
  if (!data.user?.id || !data.user.email) throw new Error('Sign in to your Safe Wallet account first.');
  return { userId: data.user.id, email: data.user.email };
}

export interface RecoverySetRow {
  id: string;
  vault_id: string;
  version: number;
  state: 'draft' | 'active' | 'retired' | 'spent' | 'cancelled';
  delay_seconds: number;
  created_at: string;
  activated_at: string | null;
}

export interface RecoveryInvitationRow {
  id: string;
  recovery_set_id: string;
  vault_id: string;
  owner_user_id: string;
  owner_email: string;
  invited_email: string;
  invited_user_id: string | null;
  custodian_public_key: string | null;
  custodian_fingerprint: string | null;
  sealed_package: string | null;
  activation_challenge_hash: string | null;
  state: 'pending' | 'accepted' | 'enrolled' | 'verified' | 'declined' | 'cancelled' | 'expired';
  expires_at: string;
}

export interface RecoveryCustodianRow {
  id: string;
  recovery_set_id: string;
  vault_id: string;
  owner_email: string;
  custodian_user_id: string;
  custodian_email: string;
  custodian_public_key: string;
  fingerprint: string;
  sealed_package: string;
  state: 'pending_verification' | 'verified' | 'active' | 'revoked';
  verified_at: string | null;
}

export interface RecoveryRequestRow {
  id: string;
  recovery_set_id: string;
  recovery_set_version: number;
  vault_id: string;
  owner_user_id: string;
  owner_email: string;
  recovering_public_key: string;
  recovering_fingerprint: string;
  state: 'delaying' | 'approved' | 'released' | 'cancelled' | 'expired' | 'completed';
  delay_until: string;
  expires_at: string;
  created_at: string;
}

export interface RecoveryNotificationRow {
  id: string;
  event_type: string;
  subject: string;
  body_text: string;
  state: string;
  created_at: string;
}

export interface RecoveryOverview {
  sets: RecoverySetRow[];
  invitations: RecoveryInvitationRow[];
  custodians: RecoveryCustodianRow[];
  requests: RecoveryRequestRow[];
  notifications: RecoveryNotificationRow[];
}

export interface RecoveryHelperProfile {
  userId: string;
  email: string;
  publicKey: string;
}

export interface RecoveryHelperLookup {
  userId: string;
  email: string;
  publicKey: string | null;
}

interface RecoveryHelperKeyRow {
  user_id: string;
  vault_id: string;
  public_key: string;
  protected_private_key: string;
}

export async function loadRecoveryOverview(vaultId: string): Promise<RecoveryOverview> {
  const api = client();
  const [sets, invitations, custodians, requests, notifications] = await Promise.all([
    api.from('recovery_sets').select('id, vault_id, version, state, delay_seconds, created_at, activated_at').eq('vault_id', vaultId).order('version', { ascending: false }),
    api.from('recovery_invitations').select('id, recovery_set_id, vault_id, owner_user_id, owner_email, invited_email, invited_user_id, custodian_public_key, custodian_fingerprint, sealed_package, activation_challenge_hash, state, expires_at').order('created_at', { ascending: false }),
    api.from('recovery_custodians').select('id, recovery_set_id, vault_id, owner_email, custodian_user_id, custodian_email, custodian_public_key, fingerprint, sealed_package, state, verified_at').order('created_at', { ascending: false }),
    api.from('recovery_requests').select('id, recovery_set_id, recovery_set_version, vault_id, owner_user_id, owner_email, recovering_public_key, recovering_fingerprint, state, delay_until, expires_at, created_at').order('created_at', { ascending: false }),
    api.from('recovery_notifications').select('id, event_type, subject, body_text, state, created_at').order('created_at', { ascending: false }).limit(10),
  ]);
  for (const result of [sets, invitations, custodians, requests, notifications]) {
    if (result.error) throw result.error;
  }
  return {
    sets: (sets.data ?? []) as RecoverySetRow[],
    invitations: (invitations.data ?? []) as RecoveryInvitationRow[],
    custodians: (custodians.data ?? []) as RecoveryCustodianRow[],
    requests: (requests.data ?? []) as RecoveryRequestRow[],
    notifications: (notifications.data ?? []) as RecoveryNotificationRow[],
  };
}

/**
 * Recovery requests waiting on this signed-in account as a trusted contact.
 *
 * Row-level security already limits both tables to the parties involved: a contact may
 * read requests for sets they are active on, and their own custodian row. Nothing here
 * widens that — it pairs each request with the caller's own row, which carries the sealed
 * share needed to approve. Requests the caller owns are excluded: recovering your own
 * wallet is never something you approve for yourself.
 */
export async function loadIncomingRecoveryApprovals(): Promise<Array<{ request: RecoveryRequestRow; contact: RecoveryCustodianRow }>> {
  const api = client();
  const { data: { user } } = await api.auth.getUser();
  if (!user) return [];

  const [requests, custodians] = await Promise.all([
    api.from('recovery_requests')
      .select('id, recovery_set_id, recovery_set_version, vault_id, owner_user_id, owner_email, recovering_public_key, recovering_fingerprint, state, delay_until, expires_at, created_at')
      .in('state', ['delaying', 'approved'])
      .order('created_at', { ascending: false }),
    api.from('recovery_custodians')
      .select('id, recovery_set_id, vault_id, owner_email, custodian_user_id, custodian_email, custodian_public_key, fingerprint, sealed_package, state, verified_at')
      .eq('custodian_user_id', user.id)
      .eq('state', 'active'),
  ]);
  if (requests.error) throw requests.error;
  if (custodians.error) throw custodians.error;

  const mine = (custodians.data ?? []) as RecoveryCustodianRow[];
  return ((requests.data ?? []) as RecoveryRequestRow[])
    .filter((request) => request.owner_user_id !== user.id)
    .flatMap((request) => {
      const contact = mine.find((row) => row.recovery_set_id === request.recovery_set_id);
      return contact ? [{ request, contact }] : [];
    });
}

export async function verifyWalletPin(params: {
  device: DeviceState;
  pin: string;
  vaultRootKey: Uint8Array;
}): Promise<void> {
  if (!/^\d{6,12}$/.test(params.pin)) throw new Error('Enter your 6 to 12 digit Safe Wallet PIN.');
  const deviceSecret = await loadDeviceSecret(params.device);
  let unlocked: Uint8Array | null = null;
  try {
    unlocked = await unlockVaultRootKey(params.pin, deviceSecret, params.device.pinEnvelope);
    if (unlocked.byteLength !== params.vaultRootKey.byteLength) throw new Error('Incorrect Safe Wallet PIN.');
    let difference = 0;
    for (let index = 0; index < unlocked.byteLength; index += 1) {
      difference |= unlocked[index] ^ params.vaultRootKey[index];
    }
    if (difference !== 0) throw new Error('Incorrect Safe Wallet PIN.');
  } catch (cause) {
    if (cause instanceof Error && cause.message === 'Incorrect Safe Wallet PIN.') throw cause;
    throw new Error('Incorrect Safe Wallet PIN.');
  } finally {
    await clearBytes(unlocked);
    await clearBytes(deviceSecret);
  }
}

async function loadOwnRecoveryHelperKey(): Promise<RecoveryHelperKeyRow | null> {
  const { data, error } = await client()
    .from('recovery_helper_keys')
    .select('user_id, vault_id, public_key, protected_private_key')
    .maybeSingle();
  if (error) throw error;
  return (data as RecoveryHelperKeyRow | null) ?? null;
}

async function importOwnRecoveryHelper(vaultRootKey: Uint8Array): Promise<SoftwareRecoveryCustodian> {
  const account = await currentAccountIdentity();
  const stored = await loadOwnRecoveryHelperKey();
  if (!stored) throw new Error('Your recovery-contact profile is not ready. Open Safe Wallet again and retry.');
  const privateKeyPkcs8 = await openRecoveryCustodianPrivateKey({
    vaultRootKey,
    userId: account.userId,
    publicKey: stored.public_key,
    protectedPrivateKey: JSON.parse(stored.protected_private_key) as VaultProtectedRecoveryKey,
  });
  try {
    return await importRecoveryCustodian(stored.public_key, privateKeyPkcs8);
  } finally {
    privateKeyPkcs8.fill(0);
  }
}

export async function ensureRecoveryHelperIdentity(params: {
  device: DeviceState;
  vaultRootKey: Uint8Array;
}): Promise<void> {
  const account = await currentAccountIdentity();
  const existing = await loadOwnRecoveryHelperKey();
  if (existing) {
    const privateKeyPkcs8 = await openRecoveryCustodianPrivateKey({
      vaultRootKey: params.vaultRootKey,
      userId: account.userId,
      publicKey: existing.public_key,
      protectedPrivateKey: JSON.parse(existing.protected_private_key) as VaultProtectedRecoveryKey,
    });
    privateKeyPkcs8.fill(0);
    return;
  }

  const exported = await createExportableRecoveryCustodian();
  try {
    const protectedPrivateKey = await protectRecoveryCustodianPrivateKey({
      vaultRootKey: params.vaultRootKey,
      userId: account.userId,
      publicKey: exported.publicKey,
      privateKeyPkcs8: exported.privateKeyPkcs8,
    });
    const { error } = await client().rpc('register_recovery_helper_key', {
      p_vault_id: params.device.vaultId,
      p_device_id: params.device.deviceId,
      p_device_auth_token: params.device.deviceAuthToken,
      p_public_key: exported.publicKey,
      p_protected_private_key: JSON.stringify(protectedPrivateKey),
    });
    if (error) throw error;
  } finally {
    exported.privateKeyPkcs8.fill(0);
  }
}

export async function lookupRecoveryHelperProfile(email: string): Promise<RecoveryHelperLookup | null> {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return null;
  const { data, error } = await client().rpc('lookup_recovery_helper_profile', { p_email: normalized });
  if (error) throw error;
  if (!data) return null;
  const value = data as { user_id?: string; email?: string; public_key?: string | null };
  if (!value.user_id || !value.email) return null;
  return { userId: value.user_id, email: value.email, publicKey: value.public_key ?? null };
}

export async function createRecoveryDraft(params: {
  device: DeviceState;
  vaultRootKey: Uint8Array;
  delaySeconds?: number;
}): Promise<string> {
  const recoverySetId = crypto.randomUUID();
  const provisioning = await createRecoveryDraftProvisioning(params.vaultRootKey, recoverySetId);
  const { error } = await client().rpc('create_recovery_draft', {
    p_set_id: recoverySetId,
    p_vault_id: params.device.vaultId,
    p_device_id: params.device.deviceId,
    p_device_auth_token: params.device.deviceAuthToken,
    p_recovery_envelope: JSON.stringify(provisioning.envelope),
    p_server_share: provisioning.serverShareBase64,
    p_completion_secret_hash: provisioning.completionSecretHash,
    p_delay_seconds: params.delaySeconds ?? 300,
  });
  if (error) throw error;
  await saveRecoveryDraft({
    version: 2,
    recoverySetId,
    encryptedDraft: provisioning.encryptedLocalDraft,
    createdAt: nowIso(),
  });
  return recoverySetId;
}

export async function cancelRecoveryDraft(device: DeviceState, recoverySetId: string): Promise<void> {
  const { error } = await client().rpc('cancel_recovery_draft', {
    p_set_id: recoverySetId,
    p_device_id: device.deviceId,
    p_device_auth_token: device.deviceAuthToken,
  });
  if (error) throw error;
  await clearRecoveryDraft(recoverySetId);
}

export async function sendRecoveryInvitation(params: {
  device: DeviceState;
  vaultRootKey: Uint8Array;
  recoverySetId: string;
  helper: RecoveryHelperProfile;
}): Promise<void> {
  const draft = await loadRecoveryDraft(params.recoverySetId);
  if (!draft) {
    throw new Error('The recovery setup key is not available on this device. Start a new recovery setup.');
  }
  const share = await openLocalRecoveryDraft(
    params.vaultRootKey,
    params.recoverySetId,
    draft.encryptedDraft,
  );
  try {
    const contact = await createRecoveryContactProvisioning({
      recoverySetId: params.recoverySetId,
      custodianShare: share,
      custodianPublicKey: params.helper.publicKey,
    });
    const { error } = await client().rpc('create_recovery_invitation_v2', {
      p_id: crypto.randomUUID(),
      p_set_id: params.recoverySetId,
      p_device_id: params.device.deviceId,
      p_device_auth_token: params.device.deviceAuthToken,
      p_invited_email: params.helper.email,
      p_custodian_public_key: params.helper.publicKey,
      p_custodian_fingerprint: contact.fingerprint,
      p_sealed_package: JSON.stringify(contact.sealedPackage),
      p_activation_challenge_hash: contact.activationChallengeHash,
      p_expires_at: new Date(Date.now() + 14 * 24 * 60 * 60_000).toISOString(),
    });
    if (error) throw error;
  } finally {
    share.fill(0);
  }
}

export async function acceptRecoveryInvitation(params: {
  invitation: RecoveryInvitationRow;
  device: DeviceState;
  vaultRootKey: Uint8Array;
}): Promise<void> {
  if (!params.invitation.sealed_package || !params.invitation.activation_challenge_hash) {
    throw new Error('This invitation was created with an older recovery setup. Ask the owner to send a new invitation.');
  }
  await ensureRecoveryHelperIdentity({ device: params.device, vaultRootKey: params.vaultRootKey });
  const custodian = await importOwnRecoveryHelper(params.vaultRootKey);
  const activationChallenge = await verifyRecoveryContactPackage({
    custodian,
    recoverySetId: params.invitation.recovery_set_id,
    sealedPackage: JSON.parse(params.invitation.sealed_package) as SealedRecoveryValue,
  });
  const { error } = await client().rpc('accept_recovery_invitation_v2', {
    p_invitation_id: params.invitation.id,
    p_device_id: params.device.deviceId,
    p_device_auth_token: params.device.deviceAuthToken,
    p_activation_challenge: activationChallenge,
  });
  if (error) throw error;
}

export async function declineRecoveryInvitation(invitationId: string): Promise<void> {
  const { error } = await client().rpc('decline_recovery_invitation_v2', {
    p_invitation_id: invitationId,
  });
  if (error) throw error;
}

export async function startWalletRecovery(email: string): Promise<{ requestId: string; fingerprint: string; delayUntil: string }> {
  const account = await currentAccountIdentity();
  if (account.email.toLowerCase() !== email.trim().toLowerCase()) throw new Error('The verified account does not match this email.');
  const { data: vaultId, error: vaultError } = await client().rpc('my_vault_id');
  if (vaultError) throw vaultError;
  if (!vaultId) throw new Error('This account does not have a Safe Wallet.');
  const recipient = await createRecoveryRecipient();
  const fingerprint = await recoveryKeyFingerprint(recipient.publicKey);
  const requestId = crypto.randomUUID();
  const { data, error } = await client().rpc('start_wallet_recovery', {
    p_request_id: requestId,
    p_vault_id: String(vaultId),
    p_recovering_public_key: recipient.publicKey,
    p_recovering_fingerprint: fingerprint,
  });
  if (error) throw error;
  const payload = data as { recovery_set_id: string; delay_until: string };
  await savePendingRecovery({
    version: 2,
    requestId,
    recoverySetId: payload.recovery_set_id,
    vaultId: String(vaultId),
    recoveringPublicKey: recipient.publicKey,
    recoveringPrivateKey: recipient.privateKey,
    fingerprint,
    ownerEmail: account.email,
    createdAt: nowIso(),
  });
  return { requestId, fingerprint, delayUntil: payload.delay_until };
}

export async function cancelWalletRecovery(requestId: string): Promise<void> {
  const { error } = await client().rpc('cancel_wallet_recovery', { p_request_id: requestId });
  if (error) throw error;
  await clearPendingRecovery();
}

export async function approveWalletRecovery(params: {
  request: RecoveryRequestRow;
  contact: RecoveryCustodianRow;
  device: DeviceState;
  vaultRootKey: Uint8Array;
}): Promise<void> {
  const expectedFingerprint = await recoveryKeyFingerprint(params.request.recovering_public_key);
  if (expectedFingerprint !== params.request.recovering_fingerprint) {
    throw new Error('The recovering-device fingerprint is invalid. Decline this request.');
  }
  const custodian = await importOwnRecoveryHelper(params.vaultRootKey);
  const context: RecoveryApprovalContext = {
    requestId: params.request.id,
    recoverySetId: params.request.recovery_set_id,
    vaultId: params.request.vault_id,
    ownerUserId: params.request.owner_user_id,
    custodianUserId: params.contact.custodian_user_id,
    custodianId: params.contact.id,
    recoveringPublicKey: params.request.recovering_public_key,
    recoverySetVersion: params.request.recovery_set_version,
    expiresAt: params.request.expires_at,
  };
  const resealed = await custodian.approveAndReseal(
    params.request.recovery_set_id,
    JSON.parse(params.contact.sealed_package) as SealedRecoveryValue,
    params.request.recovering_public_key,
    context,
  );
  const { error } = await client().rpc('approve_wallet_recovery', {
    p_request_id: params.request.id,
    p_custodian_id: params.contact.id,
    p_approving_device_id: params.device.deviceId,
    p_device_auth_token: params.device.deviceAuthToken,
    p_resealed_share: JSON.stringify(resealed),
  });
  if (error) throw error;
}

export interface ReleasedRecoveryMaterial {
  recovery_set_id: string;
  recovery_set_version: number;
  recovery_envelope: string;
  server_share: string;
  resealed_share: string;
  custodian_id: string;
  custodian_user_id: string;
  owner_user_id: string;
  vault_id: string;
  recovering_public_key: string;
  expires_at: string;
}

export async function releaseWalletRecovery(requestId: string): Promise<ReleasedRecoveryMaterial> {
  const { data, error } = await client().rpc('release_wallet_recovery', { p_request_id: requestId });
  if (error) throw error;
  return data as ReleasedRecoveryMaterial;
}

export async function completeWalletRecovery(params: {
  released: ReleasedRecoveryMaterial;
  pin: string;
  deviceName: string;
}): Promise<{ device: DeviceState; vaultRootKey: Uint8Array }> {
  const pending = await loadPendingRecovery();
  if (!pending) throw new Error('The recovering-device key is unavailable. Start recovery again on this browser.');
  if (pending.recoverySetId !== params.released.recovery_set_id
      || pending.recoveringPublicKey !== params.released.recovering_public_key) {
    throw new Error('Released recovery material does not match this device.');
  }
  const context: RecoveryApprovalContext = {
    requestId: pending.requestId,
    recoverySetId: params.released.recovery_set_id,
    vaultId: params.released.vault_id,
    ownerUserId: params.released.owner_user_id,
    custodianUserId: params.released.custodian_user_id,
    custodianId: params.released.custodian_id,
    recoveringPublicKey: params.released.recovering_public_key,
    recoverySetVersion: params.released.recovery_set_version,
    expiresAt: params.released.expires_at,
  };
  const recovered = await recoverVaultMaterial({
    envelope: JSON.parse(params.released.recovery_envelope) as RecoveryEnvelope,
    serverShareBase64: params.released.server_share,
    resealedShare: JSON.parse(params.released.resealed_share) as SealedRecoveryValue,
    recipient: { publicKey: pending.recoveringPublicKey, privateKey: pending.recoveringPrivateKey },
    context,
  });
  let deviceSecret: Uint8Array | null = null;
  try {
    deviceSecret = await createDeviceSecret();
    const deviceAuthToken = await createDeviceAuthorizationToken();
    const deviceAuthTokenHash = await hashDeviceAuthorizationToken(deviceAuthToken);
    const pinEnvelope = await createPinEnvelope(params.pin, deviceSecret, recovered.vaultRootKey);
    const account = await currentAccountIdentity();
    const timestamp = nowIso();
    const device: DeviceState = {
      version: 1,
      deviceId: crypto.randomUUID(),
      deviceName: params.deviceName.trim(),
      vaultId: params.released.vault_id,
      deviceSecret: encodeBytes(deviceSecret),
      deviceAuthToken,
      pinEnvelope,
      accountUserId: account.userId,
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    const { error } = await client().rpc('complete_wallet_recovery', {
      p_request_id: pending.requestId,
      p_completion_secret: recovered.completionSecretBase64,
      p_device_id: device.deviceId,
      p_device_name: device.deviceName,
      p_device_public_key: pending.recoveringPublicKey,
      p_device_auth_token_hash: deviceAuthTokenHash,
    });
    if (error) throw error;
    await saveDeviceState(device);
    await clearPendingRecovery();
    return { device, vaultRootKey: recovered.vaultRootKey };
  } catch (error) {
    recovered.vaultRootKey.fill(0);
    throw error;
  } finally {
    deviceSecret?.fill(0);
  }
}

export async function listWalletDevices(vaultId: string): Promise<Array<{ id: string; name: string; platform: string | null; revoked_at: string | null; last_seen_at: string | null; created_at: string }>> {
  const { data, error } = await client().from('devices').select('id, name, platform, revoked_at, last_seen_at, created_at').eq('vault_id', vaultId).order('created_at');
  if (error) throw error;
  return data ?? [];
}

export async function revokeWalletDevice(device: DeviceState, revokeDeviceId: string): Promise<void> {
  const { error } = await client().rpc('revoke_wallet_device', {
    p_vault_id: device.vaultId,
    p_current_device_id: device.deviceId,
    p_device_auth_token: device.deviceAuthToken,
    p_revoke_device_id: revokeDeviceId,
  });
  if (error) throw error;
}

export { loadPendingRecovery };
